import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './Blog.css';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const categoryParam = selectedCategory ? `&category=${encodeURIComponent(selectedCategory)}` : '';
      const [postsRes, categoriesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/posts?limit=50${categoryParam}`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      ]);

      const postsData = await postsRes.json();
      const categoriesData = await categoriesRes.json();

      setPosts(postsData.posts);
      setCategories(categoriesData.categories);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryFilter = (category) => {
    if (category === selectedCategory) {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  const getCategoryBadgeClass = (category) => {
    const badges = {
      'RPGs': 'badge-purple',
      'Walkthroughs': 'badge-blue',
      'Tips & Tricks': 'badge-cyan',
      'Reviews': 'badge-magenta',
      'Gear & Setups': 'badge-blue',
      'News': 'badge-cyan'
    };
    return badges[category] || 'badge-purple';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <main className="blog-page" data-testid="blog-page">
      <div className="container">
        {/* Header */}
        <div className="blog-header" data-testid="blog-header">
          <h1 className="blog-title" data-testid="blog-title">
            All Posts
          </h1>
          <p className="blog-description" data-testid="blog-description">
            Dive into the build. Level up your gameplay.
          </p>
        </div>

        {/* Category Filters */}
        <div className="filters-section" data-testid="filters-section">
          <button
            className={`filter-chip ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSearchParams({})}
            data-testid="filter-all"
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-chip ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryFilter(category)}
              data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="no-posts" data-testid="no-posts">
            <p>📝 No posts yet. Check back soon for gaming content!</p>
          </div>
        ) : (
          <div className="blog-grid" data-testid="blog-grid">
            {posts.map((post, index) => (
              <Link
                key={post._id}
                to={`/blog/${post.slug}`}
                className="blog-card glass-card"
                data-testid={`blog-post-${index}`}
              >
                <div className="blog-card-image">
                  <img src={post.featured_image} alt={post.title} />
                  {post.featured && (
                    <span className="featured-badge" data-testid="featured-badge">
                      ⭐ Featured
                    </span>
                  )}
                </div>
                <div className="blog-card-content">
                  <span className={`badge ${getCategoryBadgeClass(post.category)}`}>
                    {post.category}
                  </span>
                  <h2 className="blog-card-title">{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <div className="blog-card-meta">
                    <span>{new Date(post.publish_date).toLocaleDateString()}</span>
                    <span className="read-more">Read More →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export default Blog;
